import axios from 'axios';
import { setSnackbar } from '../actions/SnackbarActions';
import { getOrgById, getAllSitesByOrgId } from '../actions/OrgActions';
import { getSite } from '../actions/SiteActions';

// Action Types
const AUTHORIZE_REQUEST = 'AUTHORIZE_REQUEST';
const AUTHORIZE_SUCCESS = 'AUTHORIZE_SUCCESS';
const AUTHORIZE_FAILURE = 'AUTHORIZE_FAILURE';

const API_BASE_URL = process.env.REACT_APP_API_DOMAIN;
const API_VERSION = process.env.REACT_APP_API_VERSION;

// Initial State
const initialState = {
    loading: false,
    data: null,
    error: null,
    companies: [],
    projects: [],
};

// Reducer
const proCoreReducer = (state = initialState, action) => {
    switch (action.type) {
        case AUTHORIZE_REQUEST:
            return { ...state, loading: true, error: null };
        case AUTHORIZE_SUCCESS:
            return { ...state, loading: false, data: action.payload };
        case AUTHORIZE_FAILURE:
            return { ...state, loading: false, error: action.payload };
        case 'SET_PROCORE_COMPANIES':
            return { ...state, companies: action.payload };
        case 'CLEAR_PROCORE_COMPANIES':
            return { ...state, companies: [] };
        case 'SET_PROCORE_PROJECTS':
            return { ...state, projects: action.payload };
        case 'CLEAR_PROCORE_PROJECTS':
            return { ...state, projects: [] };
        case 'CLEAR_PROCORE':
            return initialState;
        case 'API_ERROR':
            return { ...state, error: action.payload };
        case 'API_START':
            return { ...state, loading: true };
        case 'API_END':
            return { ...state, loading: false };
        case 'CLEAR_API_ERROR':
            return { ...state, error: null };
        default:
            return state;
    }
};

export default proCoreReducer;

// Action Creators
export const authorizeRequest = () => ({ type: AUTHORIZE_REQUEST });
export const authorizeSuccess = (data) => ({ type: AUTHORIZE_SUCCESS, payload: data });
export const authorizeFailure = (error) => ({ type: AUTHORIZE_FAILURE, payload: error });

// Thunk Action
export const authorizeProCore = () => async (dispatch) => {

    const BACKEND_AUTHORIZE_URL = `${API_BASE_URL}${API_VERSION}/procore/oauth/authorize`;

    dispatch(authorizeRequest());
    try {
        const response = await axios.get(BACKEND_AUTHORIZE_URL);
        dispatch(authorizeSuccess(response.data));
        // If backend returns a successRedirect URL, perform redirection
        const success = response.data.success;
        const successRedirect = response.data.authorizationUrl;

        if (success) {
            window.location.href = successRedirect;
        }
    } catch (error) {
        dispatch(authorizeFailure(error.message));
        dispatch(setSnackbar('error', error.message));
    }
};

export const getProCoreCompanies = () => async (dispatch) => {
    const BACKEND_AUTHORIZE_URL = `${API_BASE_URL}${API_VERSION}/procore/companies`;
    dispatch(authorizeRequest());

    try {
        const response = await axios.get(BACKEND_AUTHORIZE_URL);
        dispatch(authorizeSuccess(response.data));

        dispatch({ type: 'SET_PROCORE_COMPANIES', payload: response.data });

    } catch (error) {
        dispatch(authorizeFailure(error.message));
        dispatch(setSnackbar('error', error.message));
    }
}

export const linkCompanyToOrg = (org, companyId) => async (dispatch) => {

    const linkCompanyToOrgurl = `${API_BASE_URL}${API_VERSION}/procore/link-company`;
    dispatch({ type: 'API_START' });
    try {
        await axios.post(linkCompanyToOrgurl, { orgId: org.id, companyId });
        dispatch({ type: 'API_END' });
        dispatch(getOrgById(org.id)); // Refresh the organization data
        dispatch({ type: 'CLEAR_PROCORE_COMPANIES' });
        dispatch(setSnackbar('success', 'Company linked successfully'));
    } catch (error) {
        dispatch({ type: 'API_END' });
        dispatch({ type: 'API_ERROR', payload: error.message });
        dispatch(setSnackbar('error', error.message));
    }
}

export const getProCoreProjects = (companyId) => async (
    dispatch) => {
    const BACKEND_AUTHORIZE_URL = `${API_BASE_URL}${API_VERSION}/procore/projects`;
    dispatch(authorizeRequest());

    try {
        const response = await axios.get(BACKEND_AUTHORIZE_URL, { params: { companyId } });
        dispatch(authorizeSuccess(response.data));
        dispatch({ type: 'SET_PROCORE_PROJECTS', payload: response.data });
    } catch (error) {
        dispatch(authorizeFailure(error.message));
        dispatch(setSnackbar('error', error.message));
    }
}


export const linkProcoreToProject = (projectId, procoreProjectId, companyId, orgId) => async (dispatch) => {
    const linkProcoreToProjectUrl = `${API_BASE_URL}${API_VERSION}/procore/link-project`;
    dispatch({ type: 'API_START' });
    try {
        await axios.post(linkProcoreToProjectUrl, { argyleProjectId: projectId, projectId: procoreProjectId, companyId });
        dispatch({ type: 'API_END' });
        dispatch(setSnackbar('success', 'Project linked successfully'));
        dispatch(getAllSitesByOrgId(orgId)); // Refresh the site data
    }
    catch (error) {

        dispatch({ type: 'API_END' });
        dispatch({ type: 'API_ERROR', payload: error.message });
        dispatch(setSnackbar('error', error.response.data.message));
    }
}

export const syncProjectObservationsToProcore = (projectId) => async (dispatch) => {
    const syncUrl = `${API_BASE_URL}${API_VERSION}/procore/sync-observations`;
    dispatch({ type: 'API_START' });
    try {
        await axios.get(syncUrl, { params: { argyleProjectId: projectId } });
        dispatch({ type: 'API_END' });
        dispatch(setSnackbar('success', 'Observations synced successfully'));
        dispatch(getSite(projectId)); // Refresh the site data
    }
    catch (error) {
        dispatch({ type: 'API_END' });
        dispatch({ type: 'API_ERROR', payload: error.message });
        dispatch(setSnackbar('error', error.message));
    }
};

export const disconnectProcoreFromProject = (projectId, orgId) => async (dispatch) => {
    const disconnectUrl = `${API_BASE_URL}${API_VERSION}/procore/disconnect-project`;
    dispatch({ type: 'API_START' });
    try {
        await axios.post(disconnectUrl, {
            procoreProjectId: projectId
        });
        dispatch({ type: 'API_END' });
        dispatch(setSnackbar('success', 'Procore project disconnected successfully'));
        dispatch(getAllSitesByOrgId(orgId)); // Refresh the site data
    } catch (error) {
        dispatch({ type: 'API_END' });
        dispatch({ type: 'API_ERROR', payload: error.message });
        dispatch(setSnackbar('error', error.message));
    }
};


export const disconnectProcoreFromOrg = (orgId) => async (dispatch) => {
    const disconnectUrl = `${API_BASE_URL}${API_VERSION}/procore/disconnect-company`;
    dispatch({ type: 'API_START' });
    try {
        await axios.post(disconnectUrl, { argyleOrgId: orgId });
        dispatch({ type: 'API_END' });
        dispatch(setSnackbar('success', 'Procore organization disconnected successfully'));
        await dispatch(getAllSitesByOrgId(orgId));
        dispatch({ type: 'CLEAR_PROCORE_COMPANIES' });
    } catch (error) {
        dispatch({ type: 'API_END' });
        dispatch({ type: 'API_ERROR', payload: error.message });
        dispatch(setSnackbar('error', error.message));
    }
};

export const disconnectProcore = () => async (dispatch) => {
    const disconnectUrl = `${API_BASE_URL}${API_VERSION}/procore/disconnect`;
    dispatch({ type: 'API_START' });
    try {
        await axios.post(disconnectUrl);
        dispatch({ type: 'API_END' });
        dispatch(setSnackbar('success', 'Procore account disconnected successfully'));
        dispatch({ type: 'CLEAR_PROCORE' });
    } catch (error) {
        dispatch({ type: 'API_END' });
        dispatch({ type: 'API_ERROR', payload: error.message });
        dispatch(setSnackbar('error', error.message));
    }
};